import React, { useState } from 'react';
import { useAuthenticatedUserData } from "contexts/user-authentication";
import { BreadCrumb } from 'primereact/breadcrumb';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { cs } from 'utils/classnames';
import { useToast } from 'contexts/toast';
import { useLocation, useNavigate } from 'react-router-dom';

export function Account () {
  const { user, isAuthenticated, updateUserData, updatingUser } = useAuthenticatedUserData();
  const location = useLocation();
  const navigate = useNavigate();
  if (!isAuthenticated || !user) return null;

  const hasMissingInformation = hasMissingAccountInformation(user.userDetails);

  const handleUpdateUserData = async (fields: Record<string, string>) => {
    await updateUserData(fields);
    if (!window.URLSearchParams) return;
    const searchParams = new window.URLSearchParams(location.search);
    const backTo = searchParams.get('backTo');
    if (!backTo) return;
    navigate(backTo, { replace: true });
  }

  return (
    <div
      style={{ maxWidth: "25rem" }}
      className="mx-auto w-full px-3"
    >
      <BreadCrumb
        className="border-0 pl-0"
        model={[{ label: "Account" }]}
        home={{ icon: "pi pi-user" }}
      />
      {hasMissingInformation ? (
        <div className='my-3 p-2' style={{background: "#8B0000", color: "white"}}>
          We require some information before you can start selling </div>
      ) : null}
      <div className='mt-2'>
        <AccountForm {...user.userDetails} onSubmit={handleUpdateUserData} working={updatingUser} />
      </div>
    </div>
  )
}

type AccountFormPropsT = {
  name?: string | null,
  phoneNumber?: string | null,
  address?: string | null,
  city?: string | null,
  landmark?: string | null,
  instagram?: string | null,
  working: boolean,
  onSubmit: (fields: Record<string, string>) => Promise<void>
}

function AccountForm (props: AccountFormPropsT) {
  const [name, setName] = useState(props.name || '');
  const [phoneNumber, setPhoneNumber] = useState((props.phoneNumber || ''));
  const [address, setAddress] = useState(props.address || '');
  const [city, setCity] = useState(props.city || '');
  const [landmark, setLandmark] = useState(props.landmark || '');
  const [instagram, setInstagram] = useState(props.instagram || '');

  const [errors, setErrors] = useState<Record<string, string>>({});

  const toast = useToast();

  const calculateErrors = () => {
    const errors: Record<string, string> = {};
    if (!name) errors.name = 'Full Name is required';
    if (!phoneNumber || phoneNumber.length !== 10) errors.phoneNumber = 'Phone number must be exactly 10 digits';
    if (!city) errors.city = 'City is required';
    if (!address) errors.address = 'Address is required';
    return errors;
  }

  const handleSubmit = async () => {
    const errors = calculateErrors();
    setErrors(errors);
    if (Object.keys(errors).length > 0) return;
    try {
      await props.onSubmit({
        name,
        phoneNumber,
        address,
        city,
        landmark,
        instagram
      });
      toast?.show({ severity: "success", detail: "Account Details Updated", life: 2000 });
    } catch (e) {
      toast?.show({ severity: "error", detail: "There was an error while updating your account details", life: 3000 });
    }
  }

  return (
    <div>
      <div className='mb-3'><small> Fields marked with * are required </small></div>
      <TextInputField label="Full Name *" setter={setName} value={name} error={errors.name} disabled={props.working} />
      <NumberInputField label="Phone Number *" setter={setPhoneNumber} value={phoneNumber} maxLength={10} error={errors.phoneNumber} disabled={props.working} />
      <TextInputField label="Instagram Handle" setter={setInstagram} value={instagram} error={errors.instagram} disabled={props.working} />
      <TextInputField label="City *" setter={setCity} value={city} error={errors.city} disabled={props.working} />
      <TextAreaInputField label="Address *" setter={setAddress} value={address} error={errors.address} disabled={props.working} />
      <TextInputField label="Landmark" setter={setLandmark} value={landmark} error={errors.landmark} disabled={props.working} />
      <Button label="Update" onClick={handleSubmit} iconPos="right" loading={props.working} disabled={props.working} />
    </div>
  );
}

function TextInputField ({
  label,
  value,
  setter,
  error,
  disabled
}: { label: string, value: string, setter: React.Dispatch<React.SetStateAction<string>>, error?: string, disabled?: boolean }) {
  return (
    <div className="field">
      <label htmlFor={label} className="block">{label}</label>
      <InputText id={label} value={value} onChange={(e) => setter(e.target.value)} className={cs('w-full', { 'p-invalid': !!error })} disabled={disabled} />
      <small id={label + '-help'} className="p-error block">{error}</small>
    </div>
  )
};

function TextAreaInputField ({
  label,
  value,
  setter,
  error,
  disabled
}: { label: string, value: string, setter: React.Dispatch<React.SetStateAction<string>>, error?: string, disabled?: boolean }) {
  return (
    <div className="field">
      <label htmlFor={label} className="block">{label}</label>
      <InputTextarea id={label} value={value} onChange={(e) => setter(e.target.value)} autoResize className={cs('w-full', { 'p-invalid': !!error })} disabled={disabled} />
      <small id={label + '-help'} className="p-error block">{error}</small>
    </div>
  )
};

function NumberInputField ({
  label,
  value,
  setter,
  maxLength,
  error,
  disabled
}: { label: string, value: string, setter: React.Dispatch<React.SetStateAction<string>>, maxLength?: number, error?: string, disabled?: boolean }) {
  return (
    <div className="field">
      <label htmlFor={label} className="block">{label}</label>
      <input
        className={cs('p-inputtext p-component p-filled p-inputnumber-input w-full', { 'p-invalid': !!error })}
        type="text"
        inputMode="numeric"
        value={value}
        onChange={e => {
          const stringValue = e.target.value;
          if (!stringValue) return setter('');
          const integerParsed = parseInt(stringValue);
          if (isNaN(integerParsed) || integerParsed.toString().length !== stringValue.length) return;
          if (!maxLength || stringValue.length <= maxLength) setter(stringValue);
          else setter(stringValue.slice(0, maxLength))
        }}
        disabled={disabled}
      />
      <small id={label + '-help'} className="p-error block">{error}</small>
    </div>
  )
};

export function hasMissingAccountInformation (userDetails: {
  name?: string | null,
  phoneNumber?: string | null,
  address?: string | null,
  city?: string | null,
}) {
  return (
    !userDetails.phoneNumber ||
    !userDetails.name ||
    !userDetails.address ||
    !userDetails.city
  )
}
